<template>
    <div>
        <dynamic-toolbar
            title="Maintenance Purchase Orders"
            icon="mdi-book"
            color="indigo"
            theme="dark"
            :loading="loading.pos"
        >
            <template #buttons>
                <v-btn
                    color="blue"
                    depressed
                    large
                    dark
                    :to="'/maintenance/invoices'"
                >
                    <v-icon left>mdi-invoice-list</v-icon>
                    Invoices
                </v-btn>
                <v-btn
                    color="success"
                    depressed
                    large
                    class="ml-2"
                    dark
                    @click="openDialog('new')"
                >
                    <v-icon left>mdi-plus</v-icon>
                    New Purchase Order
                </v-btn>
            </template>
        </dynamic-toolbar>

        <v-card elevation="2">
            <v-card-title>
                <v-row class="ml-5">
                    <v-col cols="12" md="1">
                        <v-select
                            v-model="filter.filterBy"
                            :items="filter.items"
                            label="Filter By"
                            prepend-inner-icon="mdi-filter"
                            @change="fetchPurchaseOrders"
                            :menu-props="{ top: false, offsetY: true }"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-select
                            v-model="search.searchBy"
                            :items="search.items"
                            label="Search By"
                            prepend-inner-icon="mdi-card-search-outline"
                            @change="fetchPurchaseOrders"
                            :menu-props="{ top: false, offsetY: true }"
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="search.input"
                            label="Search"
                            @input="triggerSearchRequest"
                            prepend-inner-icon="mdi-magnify"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-menu
                            v-model="datemenu.start"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="daterange.start"
                                    label="From"
                                    prepend-inner-icon="mdi-calendar"
                                    single-line
                                    placeholder="YYYY-MM-DD"
                                    v-mask="'####-##-##'"
                                    @input="triggerSearchRequest"
                                    v-bind="attrs"
                                    v-on="on"
                                    hint="Start date"
                                    persistent-hint
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="daterange.start"
                                no-title
                                @input="handleDatemenu(datemenu.start)"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-menu
                            v-model="datemenu.end"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="daterange.end"
                                    label="To"
                                    prepend-inner-icon="mdi-calendar"
                                    single-line
                                    placeholder="YYYY-MM-DD"
                                    v-mask="'####-##-##'"
                                    @input="triggerSearchRequest"
                                    v-bind="attrs"
                                    v-on="on"
                                    hint="End date"
                                    persistent-hint
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="daterange.end"
                                no-title
                                @input="handleDatemenu(datemenu.end)"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="1">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="success"
                                    depressed
                                    class="mt-4"
                                    dark
                                    @click="downloadReport"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-download-box</v-icon>
                                </v-btn>
                            </template>
                            <span>Download report</span>
                        </v-tooltip>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-data-table
                :headers="purchaseOrderHeaders"
                :items="purchaseOrders"
                :server-items-length="totalPurchaseOrders"
                :options.sync="options"
                @update:options="fetchPurchaseOrders"
                :loading="loading.pos"
                :items-per-page="15"
                item-key="id"
                show-expand
                single-expand
                disable-sort
                :expanded.sync="expanded"
            >
                <template v-slot:item="{ item, expand, isExpanded }">
                    <tr
                        @contextmenu.prevent="openContextMenu($event, item)"
                        :style="item.is_generic ? 'background-color: #EF9A9A;' : ''"
                    >
                        <td>
                            <v-icon @click="expand(!isExpanded)">
                                {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                        </td>
                        <td>
                            <div>
                                <v-tooltip right color="indigo">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            text
                                            small
                                            @click="copyToClipboard([item.purchase_order_number, item.description])"
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            {{ item.purchase_order_number }}
                                        </v-btn>
                                    </template>
                                    <span>{{ item.description }}</span>
                                </v-tooltip>
                            </div>
                        </td>
                        <td>
                            <div>
                                <v-chip
                                    small
                                    :color="
                                        item.po_type == 'TRAILER' ? 'primary' :
                                        item.po_type == 'TRUCK' ? 'indigo' :
                                        item.po_type == 'INVENTORY' ? 'warning' :
                                        'red darken-3'
                                    "
                                    text-color="white"
                                >
                                    {{ item.po_type ? item.po_type : item.is_generic ? 'GENERIC' : 'UNKNOWN' }}
                                    <v-icon right>
                                        {{ 
                                            item.po_type == 'TRAILER' ? 'mdi-train-car-tank' :
                                            item.po_type == 'TRUCK' ? 'mdi-truck-flatbed' :
                                            item.po_type == 'INVENTORY' ? 'mdi-package-variant-closed' :
                                            'mdi-book'
                                        }}
                                    </v-icon>
                                </v-chip>
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.invoice ? item.invoice.invoice_number : 'No invoice number'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.equipment
                                        ? item.equipment.Truck || item.equipment.Trailer
                                        : 'No equipment'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.driver ? item.driver.Name : 'No driver'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.region ? item.region.name : 'No region'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.vendor ? item.vendor.name : 'No vendor'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.category ? item.category.name : 'No category'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.user ? item.user.name : 'No user'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                {{
                                    item.approver ? item.approver.name : 'No approver'
                                }}
                            </div>
                        </td>
                        <td>
                            <div>
                                <v-chip
                                    :color="
                                        item.status == 'ACTIVE' ? 'green darken-1' :
                                        item.status == 'INACTIVE' ? 'red darken-1' : 'indigo darken-1'
                                    "
                                    text-color="white"
                                    small
                                    class="pa-2"
                                    style="font-weight: 500;"
                                >
                                    {{ item.status }}
                                </v-chip>
                            </div>
                        </td>
                        <td>
                            <div>
                                <v-icon
                                    class="mr-2"
                                    color="indigo"
                                    @click="editItem(item)"
                                >
                                    mdi-pencil
                                </v-icon>
                            </div>
                        </td>
                    </tr>
                </template>

                <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" style="background-color: #fff;padding: 0;">
                        <v-card>
                            <v-card-title class="headline ml-11 pt-6">
                                Details
                            </v-card-title>
                            <v-card-text>
                                <v-row class="ml-2">
                                    <v-col cols="12" md="2">
                                        <v-list three-line>
                                            <v-list-item
                                                ripple
                                                @click="() => {}"
                                            >
                                                <v-icon
                                                    large
                                                    color="indigo darken-2"
                                                    class="mr-3"
                                                >
                                                    mdi-clock-plus
                                                </v-icon>
                                                <v-list-item-content>
                                                    <span
                                                        class="text-uppercase font-weight-regular text-caption"
                                                    >
                                                        Created At
                                                    </span>
                                                    <div v-text="getDateFromDatetime(item.created_at)"></div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-col>
                                    <v-col cols="12" md="2">
                                        <v-list three-line>
                                            <v-list-item
                                                ripple
                                                @click="() => {}"
                                            >
                                                <v-icon
                                                    large
                                                    color="green darken-2"
                                                    class="mr-3"
                                                >
                                                    mdi-clock-check
                                                </v-icon>
                                                <v-list-item-content>
                                                    <span
                                                        class="text-uppercase font-weight-regular text-caption"
                                                    >
                                                        Approved At
                                                    </span>
                                                    <div v-text="getDateFromDatetime(item.approved_at) || 'No Approved At'"></div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-col>
                                    <v-col cols="12" md="2">
                                        <v-list three-line>
                                            <v-list-item
                                                ripple
                                                @click="() => {}"
                                            >
                                                <v-icon
                                                    large
                                                    color="gray darken-2"
                                                    class="mr-3"
                                                >
                                                    mdi-counter
                                                </v-icon>
                                                <v-list-item-content>
                                                    <span
                                                        class="text-uppercase font-weight-regular text-caption"
                                                    >
                                                        Kilometers
                                                    </span>
                                                    <div v-text="item.km || 'No KM'"></div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-col>
                                    <v-col cols="12" md="2">
                                        <v-list three-line>
                                            <v-list-item
                                                ripple
                                                @click="() => {}"
                                            >
                                                <v-icon
                                                    large
                                                    color="teal darken-2"
                                                    class="mr-3"
                                                >
                                                    mdi-currency-usd
                                                </v-icon>
                                                <v-list-item-content>
                                                    <span
                                                        class="text-uppercase font-weight-regular text-caption"
                                                    >
                                                        Estimated Cost
                                                    </span>
                                                    <div v-text="getFormattedCurrencyField(item.estimated_cost, 0)"></div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-col>
                                    <v-col cols="12" md="2" v-if="item.generic_approver">
                                        <v-list three-line>
                                            <v-list-item
                                                ripple
                                                @click="() => {}"
                                            >
                                                <v-icon
                                                    large
                                                    color="brown darken-2"
                                                    class="mr-3"
                                                >
                                                    mdi-account
                                                </v-icon>
                                                <v-list-item-content>
                                                    <span
                                                        class="text-uppercase font-weight-regular text-caption"
                                                    >
                                                        Generic Authorized By
                                                    </span>
                                                    <div 
                                                        v-text="item.generic_approver ? item.generic_approver.name : 'Unknown'"
                                                    ></div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-col>
                                </v-row>
                                <v-row class="ml-2 mt-1">
                                    <v-col cols="12" md="12">
                                        <v-list three-line>
                                            <v-list-item
                                                ripple
                                                @click="() => {}"
                                            >
                                                <v-icon
                                                    large
                                                    color="orange darken-4"
                                                    class="mr-3"
                                                >
                                                    mdi-text-box
                                                </v-icon>
                                                <v-list-item-content>
                                                    <span
                                                        class="text-uppercase font-weight-regular text-caption"
                                                    >
                                                        Description
                                                    </span>
                                                    <div v-text="item.description"></div>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </td>
                </template>
            </v-data-table>
        </v-card>

        <purchase-order-dialog
            :dialogStatus="purchaseOrderDialogStatus"
            :dialogType="dialogType"
            :purchaseOrderItem="purchaseOrderItem"
            :currentUserApp="currentUserApp"
            :collections="collections"
            :loading="loading"
            @close:dialog="purchaseOrderDialogStatus = false"
            @update:history="fetchPurchaseOrders()"
        ></purchase-order-dialog>

        <vue-context ref="purchaseOrderRecord" style="padding: 0;margin: 0;">
            <template slot-scope="selectedPurchaseOrder" v-if="selectedPurchaseOrder.data">
                <v-list dense>
                    <v-list-item 
                        @click="deleteItem(selectedPurchaseOrder.data)"
                    >
                        <v-list-item-icon>
                            <v-icon color="error">mdi-trash-can</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Delete PO</v-list-item-title>
                    </v-list-item>
                </v-list>
            </template>
        </vue-context>

        <v-dialog v-model="deleteDialog" max-width="500px">
            <v-card>
                <v-card-title class="headline">Are you sure?</v-card-title>
                <v-card-text>
                    This action will delete the element. Do you want to continue?
                    <p style="margin: 10px 0 0;padding: 0;">
                        <strong>
                            {{ 'P.O. ' + currentDeleteItem.purchase_order_number + ' will be eliminated'  }}
                        </strong>
                    </p>
                </v-card-text>

                <v-card-actions>
                    <v-btn color="indigo" outlined @click="deleteDialog = false">
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="error" dark @click="confirmDelete">
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import DynamicToolbar from '../../components/Global/Utils/DynamicToolbar.vue';
import PurchaseOrderDialog from '../../components/Maintenance/PurchaseOrderDialog.vue';
import { mask } from 'vue-the-mask';
import VueContext from 'vue-context';

import axios from 'axios';
import { validations } from '@/Utils/validation';

export default {
    name: 'MaintenancePurchaseOrders',
    directives: { mask },
    components: {
        VueContext,
        DynamicToolbar,
        PurchaseOrderDialog
    },
    data() {
        return {
            rules: validations,
            loading: {
                collections: true,
                pos: true,
            },
            purchaseOrders: [],
            purchaseOrderHeaders: [
                { text: '', value: 'data-table-expand' },
                { text: 'Purchase Order Number', value: 'purchase_order_number' },
                { text: 'P.O. Type', value: 'po_type' },
                { text: 'Invoice Number', value: 'invoice.invoice_number' },
                { text: 'Equipment', value: 'equipment' },
                { text: 'Driver', value: 'driver.Name' },
                { text: 'Region', value: 'region.name' },
                { text: 'Vendor', value: 'vendor.name' },
                { text: 'Category', value: 'category.name' },
                { text: 'Created by', value: 'user.name' },
                { text: 'Authorized by', value: 'approver.name' },
                { text: 'Status', value: 'status' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],
            expanded: [],
            usersAuthorizedToEdit: [
                'vlizarraga',
                'icastellon',
                'hrodriguez',
                'fchavarin',
                'ivillaseca'
            ],
            currentUserApp: '',
            purchaseOrderDialogStatus: false,
            dialogType: 'new',
            purchaseOrderItem: {},
            collections: {},
            totalPurchaseOrders: 0,
            options: {},
            search: {
                input: '',
                searchBy: 'purchase_order_number',
                items: [
                    {
                        text: 'Purchase Order Number',
                        value: 'purchase_order_number'
                    },
                    {
                        text: 'Invoice Number',
                        value: 'invoice_number'
                    },
                    {
                        text: 'Equipment',
                        value: 'equipment'
                    },
                    {
                        text: 'Driver',
                        value: 'driver'
                    },
                    {
                        text: 'Region',
                        value: 'region'
                    },
                    {
                        text: 'Vendor',
                        value: 'vendor'
                    },
                    {
                        text: 'Category',
                        value: 'category'
                    },
                ],
                timeout: null,
            },
            filter: {
                filterBy: 'all',
                items: [
                    {
                        text: 'Active',
                        value: 'active'
                    },
                    {
                        text: 'Inactive',
                        value: 'inactive'
                    },
                    {
                        text: 'All',
                        value: 'all'
                    },
                ],
            },
            daterange: {
                start: '',
                end: '',
            },
            datemenu: {
                start: false,
                end: false,
            },
            selectedPurchaseOrder: null,
            deleteDialog: false,
            currentDeleteItem: {},
        };
    },
    computed: {
        
    },
    mounted() {
        this.getCurrentUser();
        this.getCollections();
    },
    methods: {
        getCollections() {
            console.log('Get collections');
            axios
                .get('v2/maintenance/purchase-orders/collections')
                .then(response => {
                    this.collections = response.data;

                    for (let type of this.collections.categories) {
                        type.maintenance_expense_categories = type.maintenance_expense_categories.filter(cat => {
                            return cat.name !== 'Other fees';
                        });
                    }

                    console.log('collections ', this.collections);
                    this.loading.collections = false;
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getCurrentUser: function () {
            axios
                .get('user/current')
                .then(response => {
                    let user = response.data;
                    this.currentUserApp = user.email;
                    console.log('Current user app ', this.currentUserApp);
                })
                .catch(function(error) {
                    console.log(error);
                });
        },
        copyToClipboard(strings) {
            let text = strings.join('\n');
            navigator.clipboard.writeText(text);
            this.$store.dispatch('setSnackbar', {
                text: 'Successfully copied to clipboard!',
                color: 'success'
            });
        },
        downloadReport() {
            console.log("Download report");

            let params = {
                startDate: this.daterange.start,
                endDate: this.daterange.end,
                filterBy: this.filter.filterBy,
            };

            let filename = '';
            if (
                this.isValidDate(this.daterange.start) &&
                this.isValidDate(this.daterange.end)
            ) {
                filename = 'Purchase-Orders-From-' + this.daterange.start + '-To-' + this.daterange.end + '.xlsx';
            } else {
                filename = 'Purchase-Orders.xlsx';
            }
            params.filename = filename;

            let str = this.$store;
            str.state.isLoading = true;
            axios
                .post(
                    '/v2/maintenance/purchase-orders/download', params,
                    {
                        responseType: 'blob',
                        headers: {
                            Authorization: 'Bearer ' + this.$store.state.token
                        }
                    }
                )
                .then(response => {
                    let data = response.data;
                    let url = window.URL.createObjectURL(new Blob([data]));
                    let link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', filename);
                    document.body.appendChild(link);
                    link.click();
                    str.state.isLoading = false;
                })
                .catch(error => {
                    console.log(error);
                    str.state.isLoading = false;
                });
        },
        fetchPurchaseOrders() {
            this.loading.pos = true;

            this.options.search = this.search.input;
            this.options.searchBy = this.search.searchBy;
            this.options.filterBy = this.filter.filterBy;

            if (
                this.isValidDate(this.daterange.start) &&
                this.isValidDate(this.daterange.end)
            ) {
                console.log('Is valid date');
                this.options.startDate = this.daterange.start;
                this.options.endDate = this.daterange.end;
            } else {
                this.options.startDate = '';
                this.options.endDate = '';
            }

            const {
                page,
                itemsPerPage,
                searchBy,
                search,
                filterBy,
                startDate,
                endDate
            } = this.options;

            let params = new URLSearchParams({
                page: page || 1,
                itemsPerPage: itemsPerPage || 10,
                searchBy,
                search,
                filterBy,
                startDate,
                endDate
            });

            axios
                .get('v2/maintenance/purchase-orders/paginate?' + params.toString())
                .then(response => {
                    console.log(response.data);

                    let data = response.data.data || response.data;
                    let total = response.data.total || response.data.length;

                    this.purchaseOrders = data;
                    this.totalPurchaseOrders = total;
                })
                .catch(error => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading.pos = false;
                });
        },
        getDateFromDatetime(datetime) {
            if (datetime == null) return '';
            const date = new Date(datetime);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        },
        isAuthorizedUser() {
            return this.usersAuthorizedToEdit.includes(this.currentUserApp);
        },
        deleteItem(item) {
            console.log('Delete item: ', item);

            if (!this.isAuthorizedUser()) {
                this.$store.dispatch('setSnackbar', {
                    text: 'You do not have permissions to use this function!',
                    color: 'error'
                });
                return;
            }

            this.currentDeleteItem = item.data;
            this.deleteDialog = true;
        },
        confirmDelete() {
            axios
                .delete(
                    '/v2/maintenance/purchase-orders/' + this.currentDeleteItem.id
                )
                .then(response => {
                    this.deleteDialog = false;
                    this.$store.dispatch('setSnackbar', {
                        text: 'Purchase Order deleted',
                        color: 'success'
                    });
                    console.log('Response: ', response);
                    this.fetchPurchaseOrders();
                })
                .catch(error => {
                    console.log(error);
                    this.deleteDialog = false;
                });
        },
        getFormattedCurrencyField(value, decimals = 2) {
            let cad = new Intl.NumberFormat('en-CA', {
                style: 'currency',
                currency: 'CAD',
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });
            return cad.format(value);
        },
        openDialog(type) {
            console.log('Open purchase order dialog');
            this.dialogType = type;
            this.purchaseOrderDialogStatus = true;
        },
        editItem(item) {
            if (!this.isAuthorizedUser()) {
                this.$store.dispatch('setSnackbar', {
                    text: 'You do not have permissions to use this function!',
                    color: 'error'
                });
                return;
            }

            console.log('Edit item ', item);
            this.purchaseOrderItem = item;
            this.openDialog('edit');
        },
        triggerSearchRequest() {
            clearTimeout(this.search.timeout); // Clear previous timeout
            this.search.timeout = setTimeout(() => {
                this.fetchPurchaseOrders();
            }, 2000);
        },
        openContextMenu(event, item) {
            this.$refs.purchaseOrderRecord.open(event, { data: item });
        },
        isValidDate(dateString) {
            const regex = /^\d{4}-\d{2}-\d{2}$/;

            if (!regex.test(dateString)) {
                return false;
            }

            const date = new Date(dateString);

            const isValid =
                date instanceof Date && 
                !isNaN(date) && 
                dateString === date.toISOString().split("T")[0];

            return isValid;
        },
        handleDatemenu(type) {
            type = false;
            this.fetchPurchaseOrders();
        }
    },
    watch: {
        options: {
            handler() {
                this.fetchPurchaseOrders();
            },
            deep: true,
        },
    },
    created() {
        this.fetchPurchaseOrders();
    },
};
</script>
